import { useState, useEffect } from 'react'
import {
  Box,
  Grid,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  InputAdornment,
} from '@mui/material'
import { Modal, Button } from '@components/common'
import api from '@services/api'

const initialFormData = {
  name: '',
  part_number: '',
  description: '',
  category: '',
  department: '',
  inventory_type: 'spare_part',
  unit_of_measure: 'Each',
  current_stock: 0,
  minimum_stock_level: 0,
  reorder_point: 0,
  unit_cost: '',
  location: '',
  manufacturer: '',
  expiry_date: '',
  lot_number: '',
  storage_conditions: '',
}

const INVENTORY_TYPES = [
  { value: 'spare_part', label: 'Spare Part' },
  { value: 'reagent', label: 'Reagent' },
  { value: 'consumable', label: 'Consumable' },
  { value: 'accessory', label: 'Accessory' },
]

const UNIT_OPTIONS = [
  { value: 'Each', label: 'Each' },
  { value: 'Box', label: 'Box' },
  { value: 'Pack', label: 'Pack' },
  { value: 'Set', label: 'Set' },
  { value: 'Bottle', label: 'Bottle' },
  { value: 'Vial', label: 'Vial' },
  { value: 'Tube', label: 'Tube' },
  { value: 'Kit', label: 'Kit' },
  { value: 'Roll', label: 'Roll' },
  { value: 'Pair', label: 'Pair' },
]

const InventoryForm = ({ open, onClose, item, onSuccess }) => {
  const [formData, setFormData] = useState(initialFormData)
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState({})
  const [categories, setCategories] = useState([])
  const [departments, setDepartments] = useState([])

  const isEditing = Boolean(item)

  useEffect(() => {
    if (open) {
      fetchOptions()
      if (item) {
        setFormData({
          name: item.name || '',
          part_number: item.part_number || '',
          description: item.description || '',
          category: item.category || '',
          department: item.department || '',
          inventory_type: item.inventory_type || 'spare_part',
          unit_of_measure: item.unit_of_measure || 'Each',
          current_stock: item.current_stock || 0,
          minimum_stock_level: item.minimum_stock_level || 0,
          reorder_point: item.reorder_point || 0,
          unit_cost: item.unit_cost || '',
          location: item.location || '',
          manufacturer: item.manufacturer || '',
          expiry_date: item.expiry_date || '',
          lot_number: item.lot_number || '',
          storage_conditions: item.storage_conditions || '',
        })
      } else {
        setFormData(initialFormData)
      }
      setErrors({})
    }
  }, [open, item])

  const fetchOptions = async () => {
    try {
      const [catRes, deptRes] = await Promise.all([
        api.get('/inventory/categories/'),
        api.get('/common/departments/'),
      ])
      setCategories(catRes.data.results || catRes.data)
      setDepartments(deptRes.data.results || deptRes.data)
    } catch (error) {
      console.error('Error fetching options:', error)
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }))
    }
  }

  const validate = () => {
    const newErrors = {}
    if (!formData.name.trim()) newErrors.name = 'Name is required'
    if (!formData.part_number.trim()) newErrors.part_number = 'Part number is required'
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async () => {
    if (!validate()) return

    setLoading(true)
    try {
      const payload = {
        ...formData,
        category: formData.category || null,
        department: formData.department || null,
        unit_cost: formData.unit_cost || null,
        expiry_date: formData.expiry_date || null,
      }

      if (isEditing) {
        await api.patch(`/inventory/spare-parts/${item.id}/`, payload)
      } else {
        await api.post('/inventory/spare-parts/', payload)
      }

      onSuccess?.()
      onClose()
    } catch (error) {
      console.error('Error saving inventory item:', error)
      if (error.response?.data) {
        const backendErrors = {}
        Object.entries(error.response.data).forEach(([key, value]) => {
          backendErrors[key] = Array.isArray(value) ? value[0] : value
        })
        setErrors(backendErrors)
      }
    } finally {
      setLoading(false)
    }
  }

  // Show reagent-specific fields
  const showReagentFields = formData.inventory_type === 'reagent' || formData.inventory_type === 'consumable'

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEditing ? 'Edit Inventory Item' : 'Add Inventory Item'}
      maxWidth="md"
      actions={
        <>
          <Button variant="outlined" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={loading}>
            {isEditing ? 'Update' : 'Create'}
          </Button>
        </>
      }
    >
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Item Name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            error={Boolean(errors.name)}
            helperText={errors.name}
            required
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Part Number"
            name="part_number"
            value={formData.part_number}
            onChange={handleChange}
            error={Boolean(errors.part_number)}
            helperText={errors.part_number}
            required
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            multiline
            rows={2}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Item Type</InputLabel>
            <Select
              name="inventory_type"
              value={formData.inventory_type}
              onChange={handleChange}
              label="Item Type"
            >
              {INVENTORY_TYPES.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Category</InputLabel>
            <Select
              name="category"
              value={formData.category}
              onChange={handleChange}
              label="Category"
            >
              <MenuItem value="">None</MenuItem>
              {categories.map((cat) => (
                <MenuItem key={cat.id} value={cat.id}>
                  {cat.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Section/Unit</InputLabel>
            <Select
              name="department"
              value={formData.department}
              onChange={handleChange}
              label="Section/Unit"
            >
              <MenuItem value="">None</MenuItem>
              {departments.map((dept) => (
                <MenuItem key={dept.id} value={dept.id}>
                  {dept.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Manufacturer"
            name="manufacturer"
            value={formData.manufacturer}
            onChange={handleChange}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Unit of Measure</InputLabel>
            <Select
              name="unit_of_measure"
              value={formData.unit_of_measure}
              onChange={handleChange}
              label="Unit of Measure"
            >
              {UNIT_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Current Stock"
            name="current_stock"
            type="number"
            value={formData.current_stock}
            onChange={handleChange}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Minimum Stock Level"
            name="minimum_stock_level"
            type="number"
            value={formData.minimum_stock_level}
            onChange={handleChange}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Reorder Point"
            name="reorder_point"
            type="number"
            value={formData.reorder_point}
            onChange={handleChange}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Unit Cost"
            name="unit_cost"
            type="number"
            value={formData.unit_cost}
            onChange={handleChange}
            InputProps={{
              startAdornment: <InputAdornment position="start">UGX</InputAdornment>,
            }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Storage Location"
            name="location"
            value={formData.location}
            onChange={handleChange}
          />
        </Grid>

        {showReagentFields && (
          <>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Expiry Date"
                name="expiry_date"
                type="date"
                value={formData.expiry_date}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Lot/Batch Number"
                name="lot_number"
                value={formData.lot_number}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Storage Conditions"
                name="storage_conditions"
                value={formData.storage_conditions}
                onChange={handleChange}
                placeholder="e.g., 2-8°C"
              />
            </Grid>
          </>
        )}
      </Grid>
    </Modal>
  )
}

export default InventoryForm
